import { useEffect, useRef, useState } from "react";
import { animate, motion, stagger } from "motion/react";
import { assert } from "@greeter/assert";
import css from "./ChatSupportBubble.module.scss";

export function openSupportChatHandler(bookingId?: string, errorCode?: string) {
  return () => {
    const msg = `Hej! Jeg har problemer med booking: ${bookingId ?? "ukendt"}${
      errorCode ? ` med fejlkode: ${errorCode}` : ""
    }\n\n`;
    window.open(
      `https://m.me/Greeternightlife?text=${encodeURIComponent(msg)}`,
      "_blank"
    );
  };
}

export function ChatSupportBubble(props: {
  offsetBottom?: number;
  attributes?: any;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const yRef = useRef(0);
  const [rightOffset, setRightOffset] = useState(0);

  useEffect(() => {
    function findPosition() {
      if (!containerRef.current) return;
      const windowWidth = window.innerWidth;
      const maxWidthArea = 600;

      if (windowWidth > maxWidthArea) {
        setRightOffset((windowWidth - maxWidthArea) / 2);
      }
    }
    findPosition();
    window.addEventListener("resize", findPosition);

    return () => window.removeEventListener("resize", findPosition);
  });

  useEffect(() => {
    if (!containerRef.current) return;
    const _offsetBottom = (props.offsetBottom ?? 0) * -1;
    const y = [
      yRef.current === _offsetBottom ? 0 : yRef.current,
      _offsetBottom,
    ];
    animate(containerRef.current, {
      y,
    });
    yRef.current = _offsetBottom;
  }, [props.offsetBottom]);

  useEffect(() => {
    if (!textRef.current) return;
    animate(textRef.current, { y: [2, 0], opacity: [0, 1] });
    const v = textRef.current.querySelector("#wave");
    assert(v, "requires the wave component to run");
    const tw = 30;
    animate(
      v,
      {
        rotate: [0, 15, -15, 15, -15, 15, -15, 15, -15, 0],
        scale: [2, 2, 2, 2, 2, 2, 2, 2, 2, 1],
        x: [tw, tw, tw, tw, tw, tw, tw, tw, tw, 0],
      },
      { duration: 1.5 }
    );
    const letters = Array.from(textRef.current.querySelectorAll(".letter"));
    animate(
      letters,
      { opacity: [0, 1] },
      { delay: stagger(0.05, { startDelay: 1.5 }), duration: 0.5 }
    );
    animate(
      letters,
      { y: [0, -3, 0] },
      { delay: stagger(0.1, { startDelay: 5 }) }
    );
  }, []);

  return (
    <motion.div
      key="chat"
      ref={containerRef}
      className={css.ChatBubbleContainer}
      style={{
        right: `calc(clamp(${rightOffset}px, 0px, ${Number.MAX_SAFE_INTEGER}px) + var(--vertical-padding))`,
      }}
      onClick={() => {
        // We do this, so it's easy to simply plugin chats like Jivo or Tawk
        window.dispatchEvent(
          new CustomEvent("greeter:open-support", { detail: props.attributes })
        );
        // window.open("https://m.me/Greeternightlife", "_blank")
      }}
    >
      <div className={css.ChatBubbleContainerText} ref={textRef}>
        <span style={{ marginRight: 5 }} id="wave">
          👋
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          S
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          u
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          p
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          p
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          o
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          r
        </span>
        <span className="letter" style={{ opacity: 0 }}>
          t
        </span>
      </div>
    </motion.div>
  );
}
